import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import { environment } from "../../environments/environment";

const ERP_MESSAGE_SOURCE = "bonliva-erp" as const;
const CRM_MESSAGE_SOURCE = "bonliva-crm" as const;
const EMBED_PROTOCOL_VERSION = 1 as const;

type ErpRoute = "wr-draft" | "wr-drafts-list";

type ErpReadyMessage = {
  source: typeof ERP_MESSAGE_SOURCE;
  type: "ready";
  version: typeof EMBED_PROTOCOL_VERSION;
};

type ErpNavigateMessage = {
  source: typeof ERP_MESSAGE_SOURCE;
  type: "navigate";
  version: typeof EMBED_PROTOCOL_VERSION;
  route: ErpRoute;
  id?: string;
  path: string;
  search?: string;
};

type ErpToCrmMessage = ErpReadyMessage | ErpNavigateMessage;

type CrmNavigateMessage = {
  source: typeof CRM_MESSAGE_SOURCE;
  type: "navigate";
  version: typeof EMBED_PROTOCOL_VERSION;
  route: ErpRoute;
  id?: string;
  search?: string;
};

const isErpMessage = (data: unknown): data is ErpToCrmMessage => {
  if (!data || typeof data !== "object") return false;
  const m = data as Record<string, unknown>;
  if (m.source !== ERP_MESSAGE_SOURCE) return false;
  if (m.version !== EMBED_PROTOCOL_VERSION) return false;
  if (m.type === "ready") return true;
  if (m.type !== "navigate") return false;
  if (m.route !== "wr-draft" && m.route !== "wr-drafts-list") return false;
  if (m.id !== undefined && typeof m.id !== "string") return false;
  if (m.search !== undefined && typeof m.search !== "string") return false;
  return typeof m.path === "string";
};

const crmPathFor = (route: ErpRoute, id?: string): string => {
  if (route === "wr-draft" && id) return `/demand/${id}`;
  return "/demand";
};

const withSearch = (path: string, search?: string): string => {
  if (!search) return path;
  return `${path}?${search}`;
};

type Props = RouteComponentProps<{ id?: string }>;

export const ErpWrDraftsPage = (props: Props) => {
  const classes = useStyles({});
  const { history, location } = props;
  const id = props.match.params.id;
  const search = location.search.replace(/^\?/, "");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Fingerprint (`${id}|${search}`) of the iframe's own current location, as last
  // reported by the child over postMessage. Echo cancellation reads this: we never
  // re-notify or hard-reload the child for a state it is already in. Replaces the
  // old time-window suppression (SUPPRESS_OUTBOUND_MS), whose 150ms window resonated
  // with the message round-trip latency and let the two frames ping-pong
  // /demand <-> /demand/:id indefinitely — the visible flicker.
  const childState = useRef<string>("");
  // Last URL we deliberately wrote into `iframe.src`. Tracked separately from
  // `iframe.src` (which the browser normalises) so we can short-circuit
  // identical re-assigns and avoid double-reloading the iframe.
  const lastSyncedSrc = useRef<string | null>(null);

  const erpOrigin = useMemo(() => new URL(environment.babErpUrl).origin, []);

  const computeIframeSrc = useCallback((nextId?: string, nextSearch?: string) => {
    const base = environment.babErpUrl.replace(/\/$/, "");
    const path = nextId ? `${base}/demand/${nextId}` : `${base}/demand`;
    return withSearch(path, nextSearch);
  }, []);

  const [initialSrc] = useState(() => {
    const src = computeIframeSrc(id, search);
    lastSyncedSrc.current = src;
    return src;
  });

  const postToChild = useCallback(
    (message: CrmNavigateMessage) => {
      iframeRef.current?.contentWindow?.postMessage(message, erpOrigin);
    },
    [erpOrigin]
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== erpOrigin) return;
      if (!isErpMessage(event.data)) return;

      if (event.data.type === "ready") {
        // `ready` means the iframe (re)mounted and is asking where to navigate.
        // Some ERP builds re-emit it continuously; answering one the child has
        // already acted on re-triggers the child, which re-emits `ready`, and the
        // two frames storm postMessages (the flicker). Only answer when the child
        // isn't already at the parent's current location. Read from live history so
        // a stale closure can't misreport the target.
        const readyMatch = history.location.pathname.match(/^\/demand\/([^/]+)/);
        const readyId = readyMatch ? readyMatch[1] : undefined;
        const readySearch = history.location.search.replace(/^\?/, "");
        const readyFingerprint = `${readyId ?? ""}|${readySearch}`;
        if (childState.current === readyFingerprint) return;
        childState.current = readyFingerprint;
        postToChild({
          source: CRM_MESSAGE_SOURCE,
          type: "navigate",
          version: EMBED_PROTOCOL_VERSION,
          route: readyId ? "wr-draft" : "wr-drafts-list",
          id: readyId,
          search: readySearch
        });
        return;
      }

      const incomingId = event.data.id;
      const incomingSearch = event.data.search;
      const nextPath = crmPathFor(event.data.route, incomingId);
      // Compare against the live location so we don't false-trigger from a
      // stale `search` closure when several messages land in the same tick.
      const currentSearch = history.location.search.replace(/^\?/, "");
      // `search === undefined` means "leave it alone"; an empty string clears.
      const nextSearch = incomingSearch === undefined ? currentSearch : incomingSearch;
      const nextUrl = withSearch(nextPath, nextSearch);
      const currentUrl = withSearch(history.location.pathname, currentSearch);

      // Record where the child now is BEFORE the in-sync check, so even no-op echoes
      // keep our view of the child's location current. The outbound/reload effects
      // read this to avoid bouncing a change back to the frame that originated it.
      childState.current = `${incomingId ?? ""}|${nextSearch}`;

      if (currentUrl === nextUrl) return;

      // Use push (not replace) so the parent CRM owns the back/forward stack.
      // The iframe is configured to replace (never push) when embedded, so
      // there's exactly one history entry per user navigation — here on the
      // parent — and back/forward walks them cleanly. Replace would clobber
      // forward entries any time the iframe echoed its own state.
      history.push(nextUrl);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [history, erpOrigin, id, search, postToChild]);

  useEffect(() => {
    const fingerprint = `${id ?? ""}|${search}`;
    // The child is already at this state (it just reported it) — echoing it back
    // would re-trigger the child, which echoes again: the feedback loop. Only notify
    // the child of genuine parent-side navigations it hasn't been told about.
    if (childState.current === fingerprint) return;
    postToChild({
      source: CRM_MESSAGE_SOURCE,
      type: "navigate",
      version: EMBED_PROTOCOL_VERSION,
      route: id ? "wr-draft" : "wr-drafts-list",
      id,
      search
    });
  }, [id, search, postToChild]);

  // Reload the iframe to the target URL when the parent navigates via
  // back/forward, deep link, or programmatic push. The postMessage path
  // above is the fast/no-reload route; this is the guaranteed fallback so
  // browser back/forward always lines the iframe up with the parent URL.
  //
  // Skipped when the iframe is already showing this state (the change came from
  // the child, or we already notified it) — in that case a hard reload would be a
  // pointless flash on the common "click a draft inside the iframe" path.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const nextSrc = computeIframeSrc(id, search);
    if (lastSyncedSrc.current === nextSrc) return;
    const fingerprint = `${id ?? ""}|${search}`;
    if (childState.current === fingerprint) {
      lastSyncedSrc.current = nextSrc;
      return;
    }
    lastSyncedSrc.current = nextSrc;
    iframe.src = nextSrc;
  }, [id, search, computeIframeSrc]);

  return (
    <div className={classes.root}>
      <iframe
        ref={iframeRef}
        src={initialSrc}
        title="ERP Work Request Drafts"
        style={{ border: "none", flex: 1 }}
        width="100%"
        height="100%"
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%"
  },
  topbar: {
    flex: 0,
    marginBottom: 16
  }
}));
